import React, { useState } from 'react';
import { 
  ExclamationTriangleIcon, 
  CreditCardIcon,
  XMarkIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import { isOrganizationAdmin } from '../../../utils/planPermissions';

const PaymentFailureNotification = ({ 
  user, 
  paymentData, 
  isVisible,
  isAdminOrOwner 
}) => {
  const [isRedirecting, setIsRedirecting] = useState(false);

  if (!isVisible || !paymentData) return null;

  const handleBillingPortalRedirect = async () => {
    if (isRedirecting) return;
    
    setIsRedirecting(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/team-billing/create-billing-portal-session`,
        { returnUrl: window.location.href },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.url) {
        window.open(response.data.url, '_blank');
      } else {
        console.error('Failed to create billing portal session:', response.data);
        alert('Unable to open billing portal. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Error redirecting to billing portal:', error);
      alert('Unable to open billing portal. Please try again or contact support.');
    } finally {
      setIsRedirecting(false);
    }
  };

  const getNotificationConfig = () => {
    if (paymentData.isSubscriptionInactive) {
      const isAdmin = isOrganizationAdmin();
      return {
        icon: ExclamationTriangleIcon,
        title: 'No Active Subscription',
        message: isAdmin
          ? 'Your organization does not have an active subscription. Message sending is disabled until you resubscribe.'
          : 'Your organization does not have an active subscription. Message sending is disabled. Contact your organization admin.',
        actionText: 'Manage Subscription',
        isBlocking: true
      };
    }

    if (paymentData.paymentMethodIssue) {
      return {
        icon: CreditCardIcon,
        title: 'Payment Method Required',
        message: 'Your payment method needs to be updated to continue using the platform.',
        actionText: 'Update Payment Method',
        isBlocking: false
      };
    } else if (paymentData.nextRetryDate) {
      const retryDate = new Date(paymentData.nextRetryDate);
      const now = new Date();
      const hoursUntilRetry = Math.max(0, Math.round((retryDate - now) / (1000 * 60 * 60)));
      
      return {
        icon: ExclamationTriangleIcon,
        title: 'Payment Failed - Retrying Soon',
        message: `Your last payment failed. We'll retry ${hoursUntilRetry > 0 ? `in ${hoursUntilRetry} hour${hoursUntilRetry !== 1 ? 's' : ''}` : 'soon'}.`,
        actionText: 'Update Payment Info',
        isBlocking: false
      };
    } else {
      return {
        icon: ExclamationTriangleIcon,
        title: 'Payment Past Due',
        message: 'Your subscription payment is past due. Please update your billing information.',
        actionText: 'Update Billing',
        isBlocking: false
      };
    }
  };

  const config = getNotificationConfig();
  const IconComponent = config.icon;

  return (
    <div className="transition-all duration-300 ease-out transform translate-y-0 opacity-100">
      <div className="bg-white dark:bg-[#181818] border border-[#e0e0e0] dark:border-[#282828] rounded-3xl p-4 shadow-lg w-full">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className="flex-shrink-0 text-gray-500 dark:text-gray-400">
              <IconComponent className="w-5 h-5" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                  {config.title}
                </h3>
                <div className="relative group">
                  <InformationCircleIcon className="w-4 h-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-help transition-colors" />
                  <div className="absolute bottom-[calc(100%+8px)] left-1/2 transform -translate-x-1/2 group-hover:-translate-y-1 opacity-0 group-hover:opacity-100 transition-all duration-200 pointer-events-none z-50">
                    <div className="px-3 py-1.5 bg-[#EDEDED] dark:bg-[#161616] rounded-xl border border-[#dbdbdb] dark:border-[#2a2a2a] text-sm text-gray-600 dark:text-[#999999] whitespace-nowrap text-center relative shadow-lg">
                      Only organization owners and admins can <br /> see and manage billing and its notifications.
                      <div className="absolute -bottom-[6px] left-1/2 transform -translate-x-1/2 w-2.5 h-2.5 bg-[#EDEDED] dark:bg-[#161616] rotate-45 border-[#dbdbdb] dark:border-[#2a2a2a] rounded-br-[4px] border-b border-r"></div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-sm text-[#525252] dark:text-[#cccccc] leading-relaxed">
                {config.message}
              </p>
              
              {isOrganizationAdmin() && (
                <div className="mt-3 flex items-center gap-3">
                  <button
                    onClick={handleBillingPortalRedirect}
                    disabled={isRedirecting}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                      config.isBlocking
                        ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900 hover:bg-gray-800 dark:hover:bg-gray-100'
                        : 'bg-red-600 dark:bg-red-500 hover:bg-red-700 dark:hover:bg-red-600'
                    }`}
                  >
                    <CreditCardIcon className="w-4 h-4 mr-1" />
                    {isRedirecting ? 'Opening...' : config.actionText}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailureNotification; 